import { BookmarkSimple } from '@phosphor-icons/react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Comment, Post, SiroccoEvent } from '@sirocco/shared';
import { events as eventsApi, posts as postsApi } from '../../api/endpoints';
import { EventCard } from '../../components/EventCard';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState, Tabs } from '../../components/ui/Bits';
import { useAsync } from '../../hooks/useAsync';
import { useSwipeTabs } from '../../hooks/useSwipeTabs';
import { useTabMemory } from '../../hooks/useTabMemory';
import { timeAgo } from '../../utils/format';
import { t } from '../../i18n';

/** Les deux familles de signets, posts et evenements. */
type Kind = 'posts' | 'events';

const TABS: { value: Kind; label: string }[] = [
  { value: 'posts', label: t.fil.signets.onglets.posts },
  { value: 'events', label: t.fil.signets.onglets.evenements },
];

/**
 * Un signet de la premiere famille : un post, ou une reponse.
 *
 * Une reponse est un post a part entiere, rangee avec eux plutot que dans
 * un onglet a elle : au moment de retrouver ce qu'on a garde, on cherche
 * un contenu, pas une categorie de contenu.
 */
type Saved = {
  id: string;
  author: Post['author'];
  content: string;
  /* La photo d'un post, quand il en a une. Une reponse n'en porte pas. */
  imageUrl?: string | null;
  createdAt: string;
  /** Le fil ou le retrouver. */
  to: string;
  /*
    Ce que l'ecran d'arrivee a deja sous la main.

    Sans cela il le redemande au reseau, et le redemander coute cher :
    lire un post par son identifiant, c'est trois allers-retours en
    chaine — l'index, puis la fiche et les signets, puis l'auteur et les
    likes. On l'a deja ici, entier : le passer supprime l'attente et le
    saut de mise en page qui l'accompagnait.
  */
  etat?: { post: Post } | { comment: Comment };
  remove: () => void;
};

/**
 * Mes signets.
 *
 * Posts et evenements se rangent au meme endroit mais ne se melangent
 * pas : un selecteur bascule d'une famille a l'autre. Retirer un signet
 * fait disparaitre la carte immediatement, c'est le seul geste possible
 * ici, autant qu'il soit franc.
 */
export function BookmarksScreen() {
  const [kind, setKind] = useTabMemory<Kind>('mes-signets', 'posts', TABS);
  /* Le balayage double le clic sur le titre : c'est le geste qu'on essaie
     devant deux onglets cote a cote. */
  const zoneRef = useRef<HTMLDivElement>(null);
  useSwipeTabs(zoneRef, kind, TABS, setKind);

  const posts = useAsync(() => postsApi.bookmarks(), [], 'bookmarks:posts');
  const comments = useAsync(() => postsApi.bookmarkedComments(), [], 'bookmarks:comments');
  const events = useAsync(() => eventsApi.bookmarks(), [], 'bookmarks:events');

  /*
    Les posts et les reponses se chargent separement mais s'affichent
    ensemble, du plus recemment enregistre : chaque liste arrive deja
    triee de son cote, il ne reste qu'a les fondre.
  */
  const saved: Saved[] | undefined =
    posts.data && comments.data
      ? [
          ...posts.data.map((post) => ({
            id: post.id,
            author: post.author,
            content: post.content,
            imageUrl: post.imageUrl,
            createdAt: post.createdAt,
            to: `/app/feed/${post.id}/comments`,
            etat: { post },
            remove: () => void removePost(post),
          })),
          ...comments.data.map((comment) => ({
            id: comment.id,
            author: comment.author,
            content: comment.content,
            createdAt: comment.createdAt,
            /*
              Son propre fil, et non celui du post.

              Une reponse enregistree menait a la liste des commentaires
              du post, ou il fallait ensuite la retrouver a l'oeil parmi
              les autres — et si elle repondait a une reponse, elle n'y
              figurait meme pas. Son fil, lui, l'ouvre en tete avec toute
              sa lignee au-dessus : on remonte pour voir a quoi elle
              repond, cran par cran jusqu'au post.
            */
            to: `/app/feed/${comment.postId}/comments/${comment.id}`,
            etat: { comment },
            remove: () => void removeComment(comment),
          })),
        ].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
      : undefined;

  const loading = kind === 'posts' ? posts.loading || comments.loading : events.loading;
  const error = kind === 'posts' ? (posts.error ?? comments.error) : events.error;
  const reload =
    kind === 'posts'
      ? () => {
          posts.reload();
          comments.reload();
        }
      : events.reload;
  const count = kind === 'posts' ? saved?.length : events.data?.length;

  async function removePost(post: Post) {
    posts.setData((previous) => previous.filter((item) => item.id !== post.id));
    try {
      await postsApi.bookmark(post);
    } catch {
      posts.reload();
    }
  }

  async function removeComment(comment: Comment) {
    comments.setData((previous) => previous.filter((item) => item.id !== comment.id));
    try {
      await postsApi.bookmarkComment(comment);
    } catch {
      comments.reload();
    }
  }

  async function removeEvent(event: SiroccoEvent) {
    events.setData((previous) => previous.filter((item) => item.id !== event.id));
    try {
      await eventsApi.bookmark(event);
    } catch {
      events.reload();
    }
  }

  return (
    <SubScreen
      title={t.fil.signets.titre}
      action={
        count !== undefined && (
          <span className="feed__count">
            {t.fil.signets.compte(count)}
          </span>
        )
      }
    >
      <div ref={zoneRef}>
        <Tabs value={kind} options={TABS} onChange={setKind} />
      </div>

      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {kind === 'posts' && saved && (
        <>
          {saved.length === 0 && (
            <EmptyState
              icon={<BookmarkSimple size={38} />}
              title={t.fil.signets.aucunPost}
              hint={t.fil.signets.aucunPostAide}
            />
          )}
          <div className="feed__saved-list">
            {saved.map((item) => (
              /* La carte mene au fil : une reponse sortie de son post ne
                 veut souvent plus rien dire. */
              <Link key={item.id} to={item.to} state={item.etat} className="feed-saved">
                <header className="feed-saved__head">
                  <Avatar user={item.author} size={34} />
                  <span className="feed-saved__author">{item.author.username}</span>
                  <span className="feed-saved__time">· {timeAgo(item.createdAt)}</span>
                  <button
                    className="feed-saved__remove"
                    aria-label={t.fil.commun.retirerSignet}
                    onClick={(event) => {
                      event.preventDefault();
                      item.remove();
                    }}
                  >
                    <BookmarkSimple size={22} weight="fill" />
                  </button>
                </header>
                {item.content && <p className="feed-saved__content">{item.content}</p>}
                {/*
                  La photo, sur la carte.

                  Elle n'y etait pas : un post qui n'est qu'une image
                  s'enregistrait en une carte vide — un pseudo, une heure,
                  et rien. On ne pouvait pas reconnaitre ce qu'on avait
                  garde, alors que c'est justement l'image qu'on a voulu
                  garder.
                */}
                {item.imageUrl && (
                  <span className="feed-saved__photo">
                    <img src={item.imageUrl} alt={t.fil.commun.photoDe(item.author.username)} />
                  </span>
                )}
              </Link>
            ))}
          </div>
        </>
      )}

      {kind === 'events' && events.data && (
        <>
          {events.data.length === 0 && (
            <EmptyState
              icon={<BookmarkSimple size={38} />}
              title={t.fil.signets.aucuneSortie}
              hint={t.fil.signets.aucuneSortieAide}
            />
          )}
          {/*
            La meme carte que l'onglet Evenements, affiche comprise.

            Il y en avait une seconde ici, plus maigre et sans image :
            retrouver ce qu'on a mis de cote donnait l'impression de
            tomber sur autre chose que ce qu'on avait garde.
          */}
          <div className="feed__saved-list">
            {events.data.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                action={
                  /*
                    Le signet est le seul geste de cet ecran. Pose dans la
                    carte, qui est un lien : il faut donc retenir l'appui,
                    sinon retirer un signet ouvrirait la fiche au passage.
                  */
                  <button
                    className="feed-saved__remove"
                    aria-label={t.fil.commun.retirerSignet}
                    onClick={(clic) => {
                      clic.preventDefault();
                      clic.stopPropagation();
                      void removeEvent(event);
                    }}
                  >
                    <BookmarkSimple size={20} weight="fill" />
                  </button>
                }
              />
            ))}
          </div>
        </>
      )}
    </SubScreen>
  );
}
